/**
 * React Query hooks over infraService. Every query is keyed by the product and by each parent it depends on, and is
 * disabled until all of its parents are chosen. So when a parent changes, the children's queries change with it and
 * refetch on their own. Failures are shown with a Retry rather than retried silently.
 */
import { useQuery } from '@tanstack/react-query'
import { infraService } from '@/services/infraService'
import type { ImageComponent, InfraProduct, PublicIpQuotaQuery, VmSizesQuery } from '@/types/infrastructure'
import type { ServerRole } from '@/utils/naming'

const STALE_MS = 60_000

/**
 * What a product's queries are keyed by: its product line when it has one, else its slug. Two lines of one family share a slug
 * but not their golden images, so keying by slug alone would show one line the other's cached list.
 */
export const productId = (product: InfraProduct | null): string => product?.line ?? product?.slug ?? ''

export const infraKeys = {
  all: ['infra'] as const,
  products: () => [...infraKeys.all, 'products'] as const,
  environments: (product: string) => [...infraKeys.all, 'environments', product] as const,
  regions: (product: string, environment: string) => [...infraKeys.all, 'regions', product, environment] as const,
  resourceGroups: (product: string, environment: string, region: string) =>
    [...infraKeys.all, 'resource-groups', product, environment, region] as const,
  vnets: (product: string, environment: string, region: string, group: string) =>
    [...infraKeys.all, 'vnets', product, environment, region, group] as const,
  images: (product: string, region: string, component: ImageComponent) => [...infraKeys.all, 'images', product, region, component] as const,
  existing: (product: string, environment: string, region: string, group: string) =>
    [...infraKeys.all, 'existing', product, environment, region, group] as const,
  sqlNetwork: (product: string, environment: string, server: string) => [...infraKeys.all, 'sql-network', product, environment, server] as const,
  iisServers: (environment: string, region: string) => [...infraKeys.all, 'iis-servers', environment, region] as const,
  existingServers: (product: string, environment: string, region: string, group: string) =>
    [...infraKeys.all, 'existing-servers', product, environment, region, group] as const,
  serverSequence: (environment: string, product: string, role: string) => [...infraKeys.all, 'server-sequence', environment, product, role] as const,
  latestSqlImage: (product: string) => [...infraKeys.all, 'latest-sql-image', product] as const,
  sqlStorage: (product: string, region: string) => [...infraKeys.all, 'sql-storage', product, region] as const,
  vmSizes: (query: VmSizesQuery | null) =>
    [...infraKeys.all, 'vm-sizes', query?.environmentId ?? '', query?.regionId ?? '', query?.poolTier ?? '', query?.rds ?? false, query?.db ?? false, query?.hosts ?? 0] as const,
  publicIpQuota: (query: PublicIpQuotaQuery | null) =>
    [...infraKeys.all, 'public-ip-quota', query?.environmentId ?? '', query?.regionId ?? '', query?.count ?? 0] as const,
}

const options = { staleTime: STALE_MS, retry: false } as const
// Resource groups and VNets can be read live from Azure (staging), so what is shown may be a few seconds old, not a minute.
const liveOptions = { staleTime: 15_000, retry: false } as const

export function useInfraProducts() {
  return useQuery({ queryKey: infraKeys.products(), queryFn: () => infraService.getProducts(), ...options })
}

export function useInfraEnvironments(product: InfraProduct | null) {
  return useQuery({
    queryKey: infraKeys.environments(productId(product)),
    queryFn: () => infraService.getEnvironments(product as InfraProduct),
    enabled: !!product,
    ...options,
  })
}

export function useInfraRegions(product: InfraProduct | null, environmentId: string) {
  return useQuery({
    queryKey: infraKeys.regions(productId(product), environmentId),
    queryFn: () => infraService.getRegions(environmentId, product as InfraProduct),
    enabled: !!product && !!environmentId,
    ...options,
  })
}

export function useInfraResourceGroups(product: InfraProduct | null, environmentId: string, regionId: string) {
  return useQuery({
    queryKey: infraKeys.resourceGroups(productId(product), environmentId, regionId),
    queryFn: () => infraService.getResourceGroups(environmentId, regionId, product as InfraProduct),
    enabled: !!product && !!environmentId && !!regionId,
    ...liveOptions,
  })
}

export function useInfraVnets(product: InfraProduct | null, environmentId: string, regionId: string, resourceGroupId: string) {
  return useQuery({
    queryKey: infraKeys.vnets(productId(product), environmentId, regionId, resourceGroupId),
    queryFn: () => infraService.getVnets(environmentId, regionId, resourceGroupId, product as InfraProduct),
    enabled: !!product && !!environmentId && !!regionId && !!resourceGroupId,
    ...liveOptions,
  })
}

export function useGoldenImages(product: InfraProduct | null, regionId: string, component: ImageComponent = 'main') {
  return useQuery({
    queryKey: infraKeys.images(productId(product), regionId, component),
    queryFn: () => infraService.getGoldenImages(product as InfraProduct, regionId, component),
    enabled: !!product && !!regionId,
    ...options,
  })
}

/** The Domain level's existing environment: its database servers come with their SQL instances. */
export function useExistingEnvironment(product: InfraProduct | null, environmentId: string, regionId: string, resourceGroupId: string, enabled = true) {
  return useQuery({
    queryKey: infraKeys.existing(productId(product), environmentId, regionId, resourceGroupId),
    queryFn: () => infraService.getExistingEnvironment(product as InfraProduct, environmentId, regionId, resourceGroupId),
    enabled: enabled && !!product && !!environmentId && !!regionId && !!resourceGroupId,
    ...liveOptions,
  })
}

export function useSqlStorageRecommendation(product: InfraProduct | null, regionId: string) {
  return useQuery({
    queryKey: infraKeys.sqlStorage(productId(product), regionId),
    queryFn: () => infraService.getSqlStorageRecommendation(product as InfraProduct, regionId),
    enabled: !!product && !!regionId,
    ...options,
  })
}

export function useLatestSqlImage(product: InfraProduct | null) {
  return useQuery({
    queryKey: infraKeys.latestSqlImage(productId(product)),
    queryFn: () => infraService.getLatestSqlImage(product as InfraProduct),
    enabled: !!product,
    ...options,
  })
}

/**
 * The VM size each new server is built with in the region (the configured RDS and DB sizes, or the nearest series of the same size,
 * offered for the user to agree to). Disabled until there is something to ask. Never cached for long: the subscription's vCPU quota changes as servers are built.
 */
export function useVmSizes(query: VmSizesQuery | null) {
  return useQuery({
    queryKey: infraKeys.vmSizes(query),
    queryFn: () => infraService.getVmSizes(query as VmSizesQuery),
    enabled: query !== null,
    ...liveOptions,
  })
}

/**
 * Whether the environment's subscription has the Standard public IPs the servers asked for, in the region. Disabled until there is something
 * to ask (a public IP wanted, and an environment and region). Never cached for long: the quota is used up as servers are built, and the user
 * may have had it raised.
 */
export function usePublicIpQuotaQuery(query: PublicIpQuotaQuery | null) {
  return useQuery({
    queryKey: infraKeys.publicIpQuota(query),
    queryFn: () => infraService.getPublicIpQuota(query as PublicIpQuotaQuery),
    enabled: query !== null,
    ...liveOptions,
  })
}

/** The IIS servers that exist in an environment and region. Only asked for once Customer Portal or a component is answered Yes. */
export function useExistingIisServers(environmentId: string, regionId: string, enabled: boolean) {
  return useQuery({
    queryKey: infraKeys.iisServers(environmentId, regionId),
    queryFn: () => infraService.getExistingIisServers(environmentId, regionId),
    enabled: enabled && !!environmentId && !!regionId,
    ...options,
  })
}

/** Every RDS server and DB server of the chosen resource group and region (no SQL instance details). */
export function useExistingServers(product: InfraProduct | null, environmentId: string, regionId: string, resourceGroupId: string, enabled = true) {
  return useQuery({
    queryKey: infraKeys.existingServers(productId(product), environmentId, regionId, resourceGroupId),
    queryFn: () => infraService.getExistingServers(product as InfraProduct, environmentId, regionId, resourceGroupId),
    enabled: enabled && !!product && !!environmentId && !!regionId && !!resourceGroupId,
    ...liveOptions,
  })
}

/**
 * The next number of a server name series (the role's, in the environment's subscription: one more than the highest Azure VM name of the
 * series). Disabled until the product has a naming rule for the role. Never cached for long: a name is taken as soon as a server exists.
 */
export function useServerSequence(environmentId: string, product: InfraProduct | null, role: ServerRole, enabled = true) {
  return useQuery({
    queryKey: infraKeys.serverSequence(environmentId, productId(product), role),
    queryFn: () => infraService.getNextServerSequence(environmentId, product as InfraProduct, role),
    enabled: enabled && !!environmentId && !!product,
    ...liveOptions,
  })
}
